import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { PerEmpty } from '../../../components/custom_components/Empty'
import PerButton from '../../../components/custom_components/Button'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import { ObjectiveTemplateDto } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { TargetTemplateModalType } from '../../../components/custom_components/TargetTemplateModal/types'
import { useTargetTemplateModalStore } from '../../../components/custom_components/TargetTemplateModal/TargetTemplateModalStore'
import { DeleteTargetTemplateModal } from '../../../components/custom_components/TargetTemplateModal/DeleteTargetTemplateModal'
import { duplicateTarget, targetTemplateModalDeleteTarget } from '../../../components/custom_components/TargetTemplateModal/async'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import shallow from 'zustand/shallow'
import { showNotification } from '../../../components/custom_components/Notification/notification'

export type CompanySettingsTargetsTemplatesTableProps = {
  tableData: ObjectiveTemplateDto[]
  permissionToChangeTarget: boolean
}

export const CompanySettingsTargetsTemplatesTable: FC<CompanySettingsTargetsTemplatesTableProps> = ({
  tableData,
  permissionToChangeTarget,
}) => {
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const [t] = useTranslation()
  const openTargetTemplateModal = useTargetTemplateModalStore((state) => state.openTargetTemplateModal)
  const { tableActionButtonsRowWidth } = tableActionButtons()
  const [cancelTargetTemplate, setCancelTargetTemplate] = useState<ObjectiveTemplateDto | null>(null)
  const [isDuplicating, toggleDuplicating] = useState<boolean>(false)
  const [duplicateTargetTemplate, setDuplicateTargetTemplate] = useState<ObjectiveTemplateDto | null>(null)
  const [selected] = useCompanySettingsStore((state) => [state.selected], shallow)

  const handleDuplicateTargetTemplate = async (targetTemplate: ObjectiveTemplateDto) => {
    toggleDuplicating(true)
    setDuplicateTargetTemplate(targetTemplate)
    await duplicateTargetTemplateMethod(targetTemplate)
    setDuplicateTargetTemplate(null)
    toggleDuplicating(false)
  }

  const duplicateTargetTemplateMethod = async (targetTemplate: ObjectiveTemplateDto) => {
    const res = await duplicateTarget(selected.companyId, targetTemplate.id, selected.country)
    if (!!res.errors && !!res.errors.length) {
      showNotification({
        type: 'error',
        message: t('companySettingsView.targetTemplatesTab.table.notifications.duplicate.error.title'),
        description: t('companySettingsView.targetTemplatesTab.table.notifications.duplicate.error.description'),
      })
    }
  }

  const handleDeleteTargetTemplate = async (targetTemplate: ObjectiveTemplateDto | null) => {
    if (targetTemplate) {
      await targetTemplateModalDeleteTarget(selected.companyId, targetTemplate.id as string, selected.country)
      showNotification({
        type: 'success',
        message: t('companySettingsView.targetTemplatesTab.table.notifications.delete.title'),
        description: t('companySettingsView.targetTemplatesTab.table.notifications.delete.description'),
      })
      setCancelTargetTemplate(null)
    }
  }

  const columns = [
    {
      key: 'targets',
      title: t('companySettingsView.targetTemplatesTab.table.targets'),
      className: 'minWidth-column-xl',
      render: (rowData: ObjectiveTemplateDto) => (
        <PerText fontWeight="bold" mr="s" my="s">
          {rowData.objectiveTitle}
        </PerText>
      ),
    },
    {
      key: 'tasks',
      title: t('companySettingsView.targetTemplatesTab.table.tasks'),
      className: 'minWidth-column-xl',
      render: (rowData: ObjectiveTemplateDto) => (
        <PerSpacer mr="xxxl">
          {[...rowData.objectiveKeyResults]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((keyResult, index) => {
              return (
                <PerFlexRow align="middle" mb="xxxs" key={index} noWrap>
                  <div style={{ width: '20px', textAlign: 'center' }} className="per-flexRow--fixed">
                    <PerIcon name="circle_fill" color="secondary" size={8} />
                  </div>
                  <PerText fontWeight="bold" color={'secondary'} ml="xxs" className="per-flexRow--growShrink">
                    {keyResult.text}
                  </PerText>
                </PerFlexRow>
              )
            })}
        </PerSpacer>
      ),
    },
    {
      key: 'creator',
      title: t('companySettingsView.targetTemplatesTab.table.creator'),
      className: 'minWidth-column-l',
      render: (rowData: ObjectiveTemplateDto) => (
        <PerSpacer ml="xxs" className="overflow--hidden">
          <PerText mb="xxxs" color="default">
            {rowData.creator.firstName + ' ' + rowData.creator.lastName}
          </PerText>
          <PerText color="secondary" fontSize="xs">
            {t('companySettingsView.targetTemplatesTab.table.lastEdited') + ` ${formatDateShort(rowData.lastEditedDate)}`}
          </PerText>
          <PerText color="secondary" fontSize="xs">
            {`by ${rowData.lastModifier.firstName} ${rowData.lastModifier.lastName}`}
          </PerText>
        </PerSpacer>
      ),
    },
    {
      key: 'actions',
      title: t('companySettingsView.targetTemplatesTab.table.actions.heading'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(3),
      render: (rowData: ObjectiveTemplateDto) => {
        const rowDataInput: ObjectiveTemplateDto = {
          id: rowData.id,
          objectiveTitle: rowData.objectiveTitle,
          creator: rowData.creator,
          lastModifier: rowData.lastModifier,
          lastEditedDate: rowData.lastEditedDate,
          objectiveKeyResults: rowData.objectiveKeyResults,
          objectiveNotes: rowData.objectiveNotes,
        }
        return (
          <PerTableActionButtons
            justify="start"
            buttons={[
              {
                icon: 'edit_fill',
                color: 'primary',
                tooltip: t('companySettingsView.targetTemplatesTab.table.actions.editTooltip'),
                onClick: () => openTargetTemplateModal(TargetTemplateModalType.editTarget, rowDataInput),
                disabled: isDuplicating && !!duplicateTargetTemplate && rowData.id === duplicateTargetTemplate.id,
              },
              {
                icon: 'copy_fill',
                color: 'success',
                tooltip: t('companySettingsView.targetTemplatesTab.table.actions.duplicateTooltip'),
                onClick: () => handleDuplicateTargetTemplate(rowData),
                disabled: isDuplicating && !!duplicateTargetTemplate && rowData.id === duplicateTargetTemplate.id,
              },
              {
                icon: 'delete_fill',
                color: 'error',
                tooltip: t('companySettingsView.targetTemplatesTab.table.actions.deleteTooltip'),
                onClick: () => setCancelTargetTemplate(rowData),
                disabled: isDuplicating && !!duplicateTargetTemplate && rowData.id === duplicateTargetTemplate.id,
              },
            ]}
          />
        )
      },
    },
  ]

  const filteredColumns = columns.filter((c) => {
    return c.key !== 'actions' || permissionToChangeTarget
  })

  return (
    <>
      <DeleteTargetTemplateModal
        isOpen={!!cancelTargetTemplate}
        onClose={() => setCancelTargetTemplate(null)}
        onCancelTargetTemplate={async () => {
          await handleDeleteTargetTemplate(cancelTargetTemplate)
        }}
      />
      <Table
        className="light small viewWithTabsAndInfoBox scroll-compensation"
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={filteredColumns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="targetsNo_stroke"
              heading={t('companySettingsView.targetTemplatesTab.table.empty.heading')}
              info={permissionToChangeTarget ? t('companySettingsView.targetTemplatesTab.table.empty.info') : undefined}
              button={
                permissionToChangeTarget && (
                  <PerButton
                    text={t('companySettingsView.targetTemplatesTab.table.empty.button')}
                    before="add_stroke"
                    color="success"
                    onClick={() => openTargetTemplateModal(TargetTemplateModalType.newTarget)}
                  />
                )
              }
            />
          ),
        }}
      />
    </>
  )
}
